import { useState } from 'react'

import { ArtifactCanvasBody } from '@/components/artifact/artifact-canvas-layout'
import { EvaluationAnalysisView } from '@/components/evaluation/evaluation-analysis-view'
import { MatchDetailShell } from '@/components/evaluation/match-detail-shell'
import { PersonProfileStandingView } from '@/components/profile/person-profile-standing-view'
import { useProfileById } from '@/hooks/use-profile-by-id'
import type { SimulationProfile } from '@/hooks/use-platform-simulation'
import type { PersonMatchDetailTab } from '@/lib/match-detail-paths'

export function dataBenchDisplayName(profile: SimulationProfile): string {
  return `${profile.first_name} ${profile.last_name}`.trim() || profile.email || 'Candidate'
}

export function EmploymentReportCanvas({
  profile,
  opportunityTitle,
  initialTab,
}: {
  profile: SimulationProfile
  opportunityTitle?: string | null
  initialTab?: PersonMatchDetailTab
}) {
  const evaluationId = profile.evaluation_id || null
  const [activeTab, setActiveTab] = useState<PersonMatchDetailTab>(
    initialTab ?? (evaluationId ? 'analysis' : 'profile'),
  )
  const { profile: fullProfile } = useProfileById(profile.id)
  const label = dataBenchDisplayName(profile)

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <MatchDetailShell
          mode="person"
          title={label}
          description={opportunityTitle ? `Match for ${opportunityTitle}` : 'Person'}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasAnalysis={Boolean(evaluationId)}
          analysisPanel={
            evaluationId ? (
              <EvaluationAnalysisView evaluationId={evaluationId} subjectLabel={label} />
            ) : (
              <p className="text-sm text-muted-foreground">No analysis for this person yet.</p>
            )
          }
          secondaryPanel={
            fullProfile ? (
              <PersonProfileStandingView
                profile={fullProfile}
                showIdentityName={false}
                showAvatar={false}
                skillsTitle="Skills (self-reported)"
                className="pt-6 pb-6"
              />
            ) : (
              <p className="p-6 text-sm text-muted-foreground">
                {profile.enrich_status === 'pending' || profile.enrich_status === 'enriching'
                  ? 'Filling this profile from the CV…'
                  : 'Profile details unavailable.'}
              </p>
            )
          }
        />
      </div>
    </ArtifactCanvasBody>
  )
}
